import { CareOSLoader } from '@/components/ui/careos-loader';
import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { format } from "date-fns";
import { Loader2, Download, Trash2, Cookie, FileText, ShieldAlert } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { getCookiePrefs, setCookiePrefs, type CookiePrefs } from "@/lib/cookie-prefs";
import { DSRTimeline } from "@/components/privacy/DSRTimeline";
import { CompanyContractSection } from "@/components/privacy/CompanyContractSection";
import { AppLayout } from "@/components/layout/AppLayout";
import { useNavigate } from "react-router-dom";
import { useCompany } from "@/hooks/useCompany";
import { Building2 } from "lucide-react";

interface AcceptanceRow {
  id: string;
  document_slug: string;
  document_version: string;
  accepted_at: string;
  method: string;
}

interface DsrRow {
  id: string;
  request_type: string;
  status: string;
  received_at: string;
  due_at: string;
}

const REQUEST_TYPE_LABELS: Record<string, string> = {
  access: "Acesso aos meus dados",
  rectification: "Correção de dados",
  deletion: "Exclusão de dados",
  portability: "Portabilidade",
  revoke_consent: "Revogação de consentimento",
  info_sharing: "Informação sobre compartilhamento",
  automated_decision_review: "Revisão de decisão automatizada",
  confirmation: "Confirmação de tratamento",
};

const STATUS_LABELS: Record<string, string> = {
  received: "Recebida",
  under_review: "Em análise",
  in_progress: "Em andamento",
  fulfilled: "Concluída",
  rejected: "Rejeitada",
  extended: "Prazo prorrogado",
};

const SLUG_LABELS: Record<string, string> = {
  terms: "Termos de Uso",
  privacy: "Política de Privacidade",
  cookies: "Política de Cookies",
  "patient-consent": "Consentimento do Paciente",
  "caregiver-terms": "Termo do Cuidador",
  "b2b-contract": "Contrato B2B",
  dpa: "DPA",
};

const METHOD_LABELS: Record<string, string> = {
  signup: "Cadastro",
  web_click: "Aceite no site",
  forced_review: "Revisão obrigatória",
  invite: "Aceite via convite",
  api: "API",
  import: "Importação",
};

export default function PrivacyCenterPage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { isCompanyManager } = useCompany();
  const [acceptances, setAcceptances] = useState<AcceptanceRow[]>([]);
  const [dsrs, setDsrs] = useState<DsrRow[]>([]);
  const [cookies, setCookies] = useState<CookiePrefs>(() => getCookiePrefs());
  const [loading, setLoading] = useState(true);

  // DSR form
  const [dsrType, setDsrType] = useState<string>("access");
  const [dsrDescription, setDsrDescription] = useState("");
  const [dsrSubmitting, setDsrSubmitting] = useState(false);

  // Delete account
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  const [exporting, setExporting] = useState(false);

  const loadData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const [{ data: acc }, { data: dsr }] = await Promise.all([
      supabase
        .from("user_legal_acceptances")
        .select("id, document_slug, document_version, accepted_at, method")
        .eq("user_id", user.id)
        .order("accepted_at", { ascending: false }),
      supabase
        .from("data_subject_requests")
        .select("id, request_type, status, received_at, due_at")
        .eq("requester_user_id", user.id)
        .order("received_at", { ascending: false }),
    ]);
    setAcceptances((acc as AcceptanceRow[]) ?? []);
    setDsrs((dsr as DsrRow[]) ?? []);
    setLoading(false);
  }, [user]);

  useEffect(() => { void loadData(); }, [loadData]);

  const handleSaveCookies = () => {
    const { analytics, marketing, preferences } = cookies;
    setCookiePrefs({ analytics, marketing, preferences });
    toast.success("Preferências de cookies salvas");
  };

  const handleSubmitDsr = async () => {
    if (!user) return;
    setDsrSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("submit-dsr", {
        body: {
          requester_email: user.email,
          requester_name: user.user_metadata?.full_name ?? null,
          request_type: dsrType,
          scope: "self",
          description: dsrDescription,
          subject_identification: { user_id: user.id, email: user.email },
        },
      });
      if (error || (data as any)?.error) {
        throw new Error((data as any)?.error ?? error?.message);
      }
      toast.success("Solicitação registrada. Você receberá retorno em até 15 dias.");
      setDsrDescription("");
      void loadData();
    } catch (e) {
      toast.error(`Não foi possível registrar a solicitação: ${(e as Error).message}`);
    } finally {
      setDsrSubmitting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Sessão expirada");
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/export-my-data`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const filename = `careos-meus-dados-${user?.id}.json`;

      const { Capacitor } = await import("@capacitor/core");
      if (Capacitor.isNativePlatform()) {
        // Native (Android/iOS): save to Documents and open share sheet
        const text = await blob.text();
        const { Filesystem, Directory, Encoding } = await import("@capacitor/filesystem");
        const { Share } = await import("@capacitor/share");
        const written = await Filesystem.writeFile({
          path: filename,
          data: text,
          directory: Directory.Documents,
          encoding: Encoding.UTF8,
          recursive: true,
        });
        try {
          await Share.share({
            title: "Meus dados CareOS",
            text: "Exportação dos seus dados",
            url: written.uri,
            dialogTitle: "Salvar / compartilhar arquivo",
          });
          toast.success("Arquivo pronto. Escolha onde salvar.");
        } catch {
          toast.success(`Arquivo salvo em Documentos: ${filename}`);
        }
      } else {
        // Web: trigger browser download
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success("Download iniciado");
      }
    } catch (e) {
      toast.error(`Falha no export: ${(e as Error).message}`);
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke("delete-my-account", {
        body: { confirmation_text: deleteConfirm },
      });
      let result = data as any;
      if (error && !result) {
        // Non-2xx responses (e.g. 409 blocked_owner) carry the JSON body in error.context
        try {
          const ctx = (error as any)?.context;
          if (ctx && typeof ctx.json === "function") result = await ctx.json();
        } catch { /* ignore parse errors */ }
      }
      if (error || result?.error) {
        if (result?.error === "blocked_owner") {
          throw new Error(
            "Você ainda é responsável por pacientes ou empresas. Transfira a titularidade ou exclua-os antes de remover sua conta.",
          );
        }
        const msg = result?.message ?? result?.error ?? error?.message ?? "Erro desconhecido";
        throw new Error(msg);
      }
      toast.success("Conta excluída. Você será desconectado.");
      setTimeout(() => { void signOut(); }, 1500);
    } catch (e) {
      toast.error(`Não foi possível excluir: ${(e as Error).message}`);
      setDeleting(false);
    }
  };

  return (
    <AppLayout
      title="Centro de privacidade"
      showBack
      onBack={() => navigate("/app")}
      showFAB={false}
    >
      <div className="container max-w-4xl mx-auto py-6 px-4 space-y-4">
        <header className="space-y-1">
          <p className="text-sm text-muted-foreground">
            Gerencie seus consentimentos e exerça seus direitos como titular (LGPD art. 18).
          </p>
        </header>

        <Tabs defaultValue="acceptances" className="w-full">
        <TabsList className={`grid w-full ${isCompanyManager ? 'grid-cols-6' : 'grid-cols-5'} h-auto`}>
          <TabsTrigger value="acceptances" className="flex-col sm:flex-row gap-1 sm:gap-0 py-2 text-xs sm:text-sm">
            <FileText className="w-4 h-4 sm:mr-1" />
            <span>Aceites</span>
          </TabsTrigger>
          {isCompanyManager && (
            <TabsTrigger value="company" className="flex-col sm:flex-row gap-1 sm:gap-0 py-2 text-xs sm:text-sm">
              <Building2 className="w-4 h-4 sm:mr-1" />
              <span>Empresa</span>
            </TabsTrigger>
          )}
          <TabsTrigger value="cookies" className="flex-col sm:flex-row gap-1 sm:gap-0 py-2 text-xs sm:text-sm">
            <Cookie className="w-4 h-4 sm:mr-1" />
            <span>Cookies</span>
          </TabsTrigger>
          <TabsTrigger value="rights" className="flex-col sm:flex-row gap-1 sm:gap-0 py-2 text-xs sm:text-sm">
            <ShieldAlert className="w-4 h-4 sm:mr-1" />
            <span>Direitos</span>
          </TabsTrigger>
          <TabsTrigger value="export" className="flex-col sm:flex-row gap-1 sm:gap-0 py-2 text-xs sm:text-sm">
            <Download className="w-4 h-4 sm:mr-1" />
            <span>Exportar</span>
          </TabsTrigger>
          <TabsTrigger value="delete" className="flex-col sm:flex-row gap-1 sm:gap-0 py-2 text-xs sm:text-sm">
            <Trash2 className="w-4 h-4 sm:mr-1" />
            <span>Excluir</span>
          </TabsTrigger>
        </TabsList>

        {/* Aceites */}
        <TabsContent value="acceptances">
          <Card>
            <CardHeader>
              <CardTitle>Documentos aceitos</CardTitle>
              <CardDescription>Histórico dos termos e políticas que você aceitou.</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <CareOSLoader size="sm" />
              ) : acceptances.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhum aceite registrado.</p>
              ) : (
                <ul className="divide-y">
                  {acceptances.map((a) => (
                    <li key={a.id} className="py-2 flex justify-between text-sm">
                      <div>
                        <p className="font-medium">{SLUG_LABELS[a.document_slug] ?? a.document_slug}</p>
                        <p className="text-xs text-muted-foreground">
                          v{a.document_version} • {format(new Date(a.accepted_at), "dd/MM/yyyy HH:mm")}
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground">{METHOD_LABELS[a.method] ?? a.method}</span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Empresa (gestores) */}
        {isCompanyManager && (
          <TabsContent value="company">
            <CompanyContractSection />
          </TabsContent>
        )}

        {/* Cookies */}
        <TabsContent value="cookies">
          <Card>
            <CardHeader>
              <CardTitle>Preferências de cookies</CardTitle>
              <CardDescription>Você pode alterar suas escolhas a qualquer momento.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">Necessários</p>
                  <p className="text-xs text-muted-foreground">Sempre ativos. Indispensáveis ao funcionamento.</p>
                </div>
                <Switch checked disabled />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">Funcionais</p>
                  <p className="text-xs text-muted-foreground">Lembram suas preferências (idioma, tema).</p>
                </div>
                <Switch
                  checked={cookies.preferences}
                  onCheckedChange={(v) => setCookies({ ...cookies, preferences: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">Analytics</p>
                  <p className="text-xs text-muted-foreground">PostHog, métricas anônimas de uso.</p>
                </div>
                <Switch
                  checked={cookies.analytics}
                  onCheckedChange={(v) => setCookies({ ...cookies, analytics: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">Marketing</p>
                  <p className="text-xs text-muted-foreground">Comunicações promocionais.</p>
                </div>
                <Switch
                  checked={cookies.marketing}
                  onCheckedChange={(v) => setCookies({ ...cookies, marketing: v })}
                />
              </div>
              <Button onClick={handleSaveCookies}>Salvar preferências</Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* DSR */}
        <TabsContent value="rights">
          <Card>
            <CardHeader>
              <CardTitle>Exercer meus direitos (LGPD)</CardTitle>
              <CardDescription>Sua solicitação será respondida em até 15 dias corridos.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label>Tipo de solicitação</Label>
                <Select value={dsrType} onValueChange={setDsrType}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(REQUEST_TYPE_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Detalhes (opcional)</Label>
                <Textarea
                  rows={4}
                  value={dsrDescription}
                  onChange={(e) => setDsrDescription(e.target.value)}
                  placeholder="Descreva sua solicitação..."
                />
              </div>
              <Button onClick={handleSubmitDsr} disabled={dsrSubmitting}>
                {dsrSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Enviar solicitação
              </Button>

              {dsrs.length > 0 && (
                <div className="pt-4 border-t">
                  <p className="text-sm font-medium mb-2">Minhas solicitações</p>
                  <ul className="space-y-2">
                    {dsrs.map((d) => (
                      <li key={d.id} className="text-sm border rounded">
                        <details>
                          <summary className="cursor-pointer p-3 flex justify-between items-start gap-3 hover:bg-muted/40 rounded">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium">{REQUEST_TYPE_LABELS[d.request_type] ?? d.request_type}</p>
                              <p className="text-xs text-muted-foreground">
                                Recebida em {format(new Date(d.received_at), "dd/MM/yyyy")} • Prazo {format(new Date(d.due_at), "dd/MM/yyyy")}
                              </p>
                            </div>
                            <span className="text-xs whitespace-nowrap">{STATUS_LABELS[d.status] ?? d.status}</span>
                          </summary>
                          <div className="px-3 pb-3 pt-1 border-t">
                            <DSRTimeline requestId={d.id} />
                          </div>
                        </details>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Export */}
        <TabsContent value="export">
          <Card>
            <CardHeader>
              <CardTitle>Exportar meus dados</CardTitle>
              <CardDescription>
                Baixe um arquivo JSON com seus dados pessoais armazenados no CareOS (portabilidade — LGPD art. 18, V).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={handleExport} disabled={exporting}>
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Baixar meus dados (JSON)
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Delete */}
        <TabsContent value="delete">
          <Card>
            <CardHeader>
              <CardTitle className="text-destructive">Excluir minha conta</CardTitle>
              <CardDescription>Ação irreversível. Seus dados pessoais serão eliminados.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Alert variant="destructive">
                <AlertTitle>Antes de continuar</AlertTitle>
                <AlertDescription>
                  Se você é responsável por pacientes ou empresas, transfira ou exclua-os primeiro.
                  Aceites e registros legais são preservados pelo prazo previsto em lei para fins probatórios.
                </AlertDescription>
              </Alert>
              <div>
                <Label>Para confirmar, digite <strong>EXCLUIR MINHA CONTA</strong></Label>
                <Input
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  placeholder="EXCLUIR MINHA CONTA"
                />
              </div>
              <Button
                variant="destructive"
                disabled={deleteConfirm !== "EXCLUIR MINHA CONTA" || deleting}
                onClick={handleDeleteAccount}
              >
                {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Excluir permanentemente
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
